import React, { useState } from "react";
import Layout from "@/components/Layout";
import PrivacyAIChat from "@/components/PrivacyAIChat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, Lock, Link2Off, Bot, Database, Eye, EyeOff, Key } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const PrivacyAndHacking = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 md:px-8 lg:px-16">
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-8 px-2 md:px-8 lg:px-12">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Privacy & Security</h1>
          <p className="mt-2 text-gray-400">Secure your investigations and protect your digital footprint</p>
        </div>
    <Layout>
      <div className="py-6 space-y-8">
        <section className="space-y-4">
          <p className="text-xl text-muted-foreground">
            Protect yourself online with comprehensive resources, tools, and expert AI assistance
          </p>
        </section>

        <Tabs defaultValue="learn" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="learn">Learn</TabsTrigger>
            <TabsTrigger value="tools">Tools</TabsTrigger>
            <TabsTrigger value="guides">Guides</TabsTrigger>
            <TabsTrigger value="assistant">AI Assistant</TabsTrigger>
          </TabsList>

          <TabsContent value="learn" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <PrivacyCard 
                title="Understanding Data Privacy" 
                description="Learn how your data is collected, used, and protected online."
                icon={<Database className="h-10 w-10 text-sky-400" />}
                content={[
                  { title: "What is Data Privacy?", content: "Data privacy refers to the protection of personal information and the right to control how it's collected, used, and shared. It involves safeguarding sensitive information like your identity, financial details, browsing history, and communications." },
                  { title: "How Data is Collected", content: "Your data is collected through website cookies, app permissions, online forms, social media activities, loyalty programs, and device tracking. Many companies build detailed profiles based on your online behavior." },
                  { title: "Why Privacy Matters", content: "Privacy is fundamental to autonomy and dignity. Without privacy protection, you risk identity theft, financial fraud, manipulation through targeted content, discrimination, and loss of personal freedom." }
                ]}
              />
              
              <PrivacyCard 
                title="The Hacking Landscape" 
                description="Understand common attack vectors and tactics used by hackers."
                icon={<Shield className="h-10 w-10 text-red-500" />}
                content={[
                  { title: "Types of Cyber Attacks", content: "Phishing, malware, ransomware, man-in-the-middle attacks, DDoS, SQL injection, and social engineering are common attack vectors used to compromise systems and steal information." },
                  { title: "Who Are the Hackers?", content: "Hackers range from criminal organizations and state-sponsored actors to hacktivists and opportunists. Each has different motivations: financial gain, espionage, political statements, or simply causing disruption." },
                  { title: "Recent Attack Trends", content: "Supply chain attacks, AI-powered phishing, IoT vulnerabilities, and attacks on remote work infrastructure have become increasingly common in recent years." }
                ]}
              />
              
              <PrivacyCard 
                title="Digital Surveillance" 
                description="How governments and corporations track your digital activities."
                icon={<Eye className="h-10 w-10 text-amber-500" />}
                content={[
                  { title: "Mass Surveillance", content: "Governments worldwide conduct surveillance programs that collect communications, metadata, and internet activity on massive scales, often without specific warrants or oversight." },
                  { title: "Corporate Tracking", content: "Tech companies track user behavior across websites and apps to build detailed profiles for targeted advertising. This tracking extends to physical locations through smartphone sensors and facial recognition." },
                  { title: "The Surveillance Economy", content: "Your personal data has become a valuable commodity. Companies profit by collecting, analyzing, and selling information about your habits, preferences, and activities." }
                ]}
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <PrivacyCard 
                title="Social Engineering" 
                description="How attackers manipulate human psychology to gain access."
                icon={<Link2Off className="h-10 w-10 text-purple-500" />}
                content={[
                  { title: "Manipulation Tactics", content: "Social engineers exploit trust, curiosity, fear, and urgency to trick victims into divulging sensitive information or performing actions that compromise security." },
                  { title: "Phishing Explained", content: "Phishing attacks use deceptive emails, messages, or websites that impersonate trusted entities to steal credentials, financial information, or install malware." },
                  { title: "Red Flags to Watch For", content: "Unsolicited communications, urgent requests, grammatical errors, suspicious links/attachments, and requests for personal information are common warning signs of social engineering attempts." }
                ]}
              />
              
              <PrivacyCard 
                title="Encryption Fundamentals" 
                description="How encryption protects your data and communications."
                icon={<Lock className="h-10 w-10 text-green-500" />}
                content={[
                  { title: "What is Encryption?", content: "Encryption is the process of converting information into a code to prevent unauthorized access. It's the foundation of data security and private communications online." },
                  { title: "End-to-End Encryption", content: "E2EE ensures only the communicating parties can read messages. Even the service provider cannot access the content, providing maximum privacy for your communications." },
                  { title: "Encryption Best Practices", content: "Use strong encryption for sensitive data storage, secure communications with HTTPS, leverage encrypted messaging apps, and employ full-disk encryption on all devices." }
                ]}
              />
              
              <PrivacyCard 
                title="Digital Footprint" 
                description="Understanding and managing what you reveal online."
                icon={<EyeOff className="h-10 w-10 text-blue-500" />}
                content={[
                  { title: "What is a Digital Footprint?", content: "Your digital footprint consists of all traces you leave online - social media posts, website visits, app usage, purchase history, and more. This footprint reveals much about your identity and behavior." },
                  { title: "Active vs. Passive Footprints", content: "You create active footprints deliberately (posting, commenting) while passive footprints are collected without your direct involvement (IP logs, cookies, location data)." },
                  { title: "Managing Your Footprint", content: "Regularly audit your online presence, use privacy-focused services, adjust privacy settings on platforms, delete old accounts, and be mindful about what you share publicly." }
                ]}
              />
            </div>
          </TabsContent>

          <TabsContent value="tools" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Password Security</CardTitle>
                    <Key className="h-6 w-6 text-sky-400" />
                  </div>
                  <CardDescription>Tools to manage and secure your passwords</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Password Managers</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://bitwarden.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Bitwarden</a> - Free and open-source</li>
                      <li><a href="https://1password.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">1Password</a> - Feature-rich with family plans</li>
                      <li><a href="https://www.keepassx.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">KeePassXC</a> - Offline and open-source</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Password Strength Checkers</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://haveibeenpwned.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Have I Been Pwned</a> - Check if your accounts were compromised</li>
                      <li><a href="https://bitwarden.com/password-strength/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Bitwarden Password Strength Test</a></li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Two-Factor Authentication</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://authy.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Authy</a> - Multi-device 2FA</li>
                      <li><a href="https://www.yubico.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">YubiKey</a> - Hardware security keys</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Private Browsing</CardTitle>
                    <EyeOff className="h-6 w-6 text-green-500" />
                  </div>
                  <CardDescription>Tools to protect your browsing privacy</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Privacy-Focused Browsers</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://brave.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Brave</a> - Built-in ad and tracker blocking</li>
                      <li><a href="https://www.torproject.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Tor Browser</a> - Maximum anonymity</li>
                      <li><a href="https://www.mozilla.org/firefox/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Firefox</a> with privacy extensions</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Browser Extensions</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://privacybadger.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Privacy Badger</a> - Automatically blocks trackers</li>
                      <li><a href="https://ublockorigin.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">uBlock Origin</a> - Efficient ad blocker</li>
                      <li><a href="https://www.ghostery.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Ghostery</a> - Tracker visibility and blocking</li>
                      <li><a href="https://www.eff.org/https-everywhere" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">HTTPS Everywhere</a> - Enforce secure connections</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Search Engines</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://duckduckgo.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">DuckDuckGo</a> - Privacy-focused search</li>
                      <li><a href="https://brave.com/search/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Brave Search</a> - Independent index</li>
                      <li><a href="https://startpage.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Startpage</a> - Google results with privacy</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Network Security</CardTitle>
                    <Shield className="h-6 w-6 text-red-500" />
                  </div>
                  <CardDescription>Tools to secure your network connections</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">VPN Services</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://mullvad.net/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Mullvad</a> - Privacy-focused with anonymous accounts</li>
                      <li><a href="https://protonvpn.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">ProtonVPN</a> - Free tier available</li>
                      <li><a href="https://www.ivpn.net/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">IVPN</a> - No logs, audited security</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">DNS Services</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://1.1.1.1/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Cloudflare 1.1.1.1</a> - Fast and private DNS</li>
                      <li><a href="https://quad9.net/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Quad9</a> - Security-focused DNS with malicious domain blocking</li>
                      <li><a href="https://nextdns.io/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">NextDNS</a> - Customizable DNS filtering</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Network Analysis</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://www.wireshark.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Wireshark</a> - Monitor network traffic</li>
                      <li><a href="https://www.grc.com/shieldsup" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">ShieldsUP!</a> - Test firewall security</li>
                      <li><a href="https://www.advanced-ip-scanner.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Advanced IP Scanner</a> - Scan your local network</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Secure Communications</CardTitle>
                    <Lock className="h-6 w-6 text-blue-500" />
                  </div>
                  <CardDescription>Tools for private messaging and emails</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Encrypted Messaging</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://signal.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Signal</a> - End-to-end encrypted messaging</li>
                      <li><a href="https://element.io/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Element</a> - Decentralized Matrix-based messaging</li>
                      <li><a href="https://status.im/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Status</a> - Private messaging with crypto features</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Secure Email Services</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://proton.me/mail" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">ProtonMail</a> - Encrypted email service</li>
                      <li><a href="https://tutanota.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Tutanota</a> - Private email with encryption</li>
                      <li><a href="https://mailbox.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Mailbox.org</a> - Privacy-focused email provider</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Email Security Tools</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://www.gnupg.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">GnuPG</a> - Email encryption</li>
                      <li><a href="https://mailvelope.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Mailvelope</a> - Browser-based PGP</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Data Protection</CardTitle>
                    <Database className="h-6 w-6 text-purple-500" />
                  </div>
                  <CardDescription>Protect your files and personal data</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Encryption Software</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://www.veracrypt.fr/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">VeraCrypt</a> - Disk encryption tool</li>
                      <li><a href="https://cryptomator.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Cryptomator</a> - Cloud storage encryption</li>
                      <li><a href="https://www.axcrypt.net/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">AxCrypt</a> - File encryption software</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Secure File Sharing</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://onionshare.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">OnionShare</a> - Anonymous file sharing</li>
                      <li><a href="https://send.vis.ee/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Firefox Send (fork)</a> - Encrypted file transfers</li>
                      <li><a href="https://tresorit.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Tresorit</a> - End-to-end encrypted file sharing</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Data Deletion Tools</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://www.bleachbit.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">BleachBit</a> - Free disk space cleaner</li>
                      <li><a href="https://eraser.heidi.ie/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Eraser</a> - Secure data removal tool</li>
                      <li><a href="https://ssd.eff.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Surveillance Self-Defense</a> - Guide by EFF</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Privacy Analysis</CardTitle>
                    <Bot className="h-6 w-6 text-amber-500" />
                  </div>
                  <CardDescription>Tools to analyze your privacy posture</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Browser Privacy Tests</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://coveryourtracks.eff.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Cover Your Tracks</a> - Test browser fingerprinting</li>
                      <li><a href="https://browserleaks.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">BrowserLeaks</a> - Comprehensive browser testing</li>
                      <li><a href="https://www.deviceinfo.me/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">DeviceInfo</a> - See what your browser reveals</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Data Breach Checkers</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://haveibeenpwned.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Have I Been Pwned</a> - Check for compromised accounts</li>
                      <li><a href="https://monitor.firefox.com/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Firefox Monitor</a> - Data breach notifications</li>
                      <li><a href="https://identitytheft.gov/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">IdentityTheft.gov</a> - US government resource</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium">Security Analyzers</h4>
                    <ul className="list-disc pl-5 text-sm">
                      <li><a href="https://observatory.mozilla.org/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">Mozilla Observatory</a> - Website security tests</li>
                      <li><a href="https://ssl-tools.net/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">SSL Tools</a> - Check website SSL configuration</li>
                      <li><a href="https://www.immuniweb.com/ssl/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">ImmuniWeb SSL Security Test</a> - SSL/TLS security test</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="guides" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-sky-500/20">
                <CardHeader>
                  <CardTitle className="text-xl">Securing Your Digital Life</CardTitle>
                  <CardDescription>Essential steps to protect yourself online</CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger>Password Security Best Practices</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Strong, unique passwords are your first line of defense against unauthorized access to your accounts.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Key Practices:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use a different password for each account</li>
                              <li>Create passwords with at least 12 characters</li>
                              <li>Include uppercase letters, lowercase letters, numbers, and symbols</li>
                              <li>Avoid personal information or common phrases</li>
                              <li>Consider using passphrases (multiple random words)</li>
                              <li>Use a password manager to generate and store complex passwords</li>
                              <li>Enable two-factor authentication wherever possible</li>
                              <li>Change passwords immediately after a breach notification</li>
                              <li>Never share passwords or store them in plain text</li>
                            </ul>
                          </div>
                          
                          <p>Remember: the strongest password is one you don't know - use a password manager to generate and remember complex passwords for you.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-2">
                      <AccordionTrigger>Recognizing Phishing Attacks</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Phishing attacks attempt to trick you into revealing sensitive information or installing malware by posing as trusted entities.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Warning Signs:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Unexpected emails or messages, even if they appear to be from known contacts</li>
                              <li>Urgent requests or threats demanding immediate action</li>
                              <li>Generic greetings instead of your name</li>
                              <li>Spelling and grammar errors</li>
                              <li>Mismatched or suspicious URLs (hover before clicking)</li>
                              <li>Requests for sensitive information</li>
                              <li>Offers that seem too good to be true</li>
                              <li>Unusual attachments or file formats</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">How to Protect Yourself:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Verify requests through official channels, not the contact information in the message</li>
                              <li>Type URLs directly instead of clicking links</li>
                              <li>Never download unexpected attachments</li>
                              <li>Use email filtering and anti-phishing tools</li>
                              <li>Keep software updated to patch vulnerabilities</li>
                              <li>Enable two-factor authentication</li>
                            </ul>
                          </div>
                          
                          <p>When in doubt, assume suspicious messages are phishing attempts and verify through official channels.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-3">
                      <AccordionTrigger>Secure Device Configuration</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Properly securing your devices is fundamental to your overall digital security and privacy.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Basic Device Security:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use strong screen lock methods (biometric, PIN, password)</li>
                              <li>Enable full-disk encryption</li>
                              <li>Keep operating systems and apps updated</li>
                              <li>Install security software (antivirus, anti-malware)</li>
                              <li>Use a standard user account for daily activities, not administrator</li>
                              <li>Disable unnecessary services and permissions</li>
                              <li>Review app permissions regularly</li>
                              <li>Enable "Find My Device" features</li>
                              <li>Back up your data regularly</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Advanced Measures:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use a firewall and configure it properly</li>
                              <li>Disable unnecessary network services</li>
                              <li>Use secure DNS services</li>
                              <li>Configure automatic updates</li>
                              <li>Set up guest networks for IoT devices</li>
                              <li>Use application whitelisting when possible</li>
                              <li>Enable USB drive encryption</li>
                            </ul>
                          </div>
                          
                          <p>Remember that security is only as strong as your weakest device, so apply these principles across all your devices.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-4">
                      <AccordionTrigger>Safe Browsing Habits</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Your browsing habits significantly impact your online security and privacy.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Safe Browsing Practices:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use HTTPS whenever possible (look for the lock icon)</li>
                              <li>Be cautious with file downloads and only download from trusted sources</li>
                              <li>Keep your browser and extensions updated</li>
                              <li>Use a privacy-focused browser or configure your browser for privacy</li>
                              <li>Clear cookies and browsing history regularly</li>
                              <li>Use separate browsers for different purposes (e.g., one for banking, another for general browsing)</li>
                              <li>Be wary of browser notifications and permission requests</li>
                              <li>Disable JavaScript for untrusted sites</li>
                              <li>Use reader mode when available to avoid malicious scripts</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Recommended Extensions:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>uBlock Origin - Ad and tracker blocking</li>
                              <li>Privacy Badger - Learns to block invisible trackers</li>
                              <li>HTTPS Everywhere - Enforces secure connections</li>
                              <li>NoScript/uMatrix - Granular control over JavaScript</li>
                              <li>Decentraleyes - Protects against CDN tracking</li>
                              <li>Cookie AutoDelete - Automatically removes cookies</li>
                            </ul>
                          </div>
                          
                          <p>Combining these practices with a privacy-focused browser will significantly enhance your online privacy and security.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
                <CardFooter>
                  <p className="text-xs text-muted-foreground">Note: Security is a continuous process, not a one-time setup. Regularly review and update your security measures.</p>
                </CardFooter>
              </Card>
              
              <Card className="border-blue-500/20">
                <CardHeader>
                  <CardTitle className="text-xl">Advanced Protection Strategies</CardTitle>
                  <CardDescription>Take your security to the next level</CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger>Digital Privacy Strategies</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Privacy goes beyond security measures - it's about controlling who has access to your data and how it's used.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Data Minimization:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Share only what's necessary - question why information is being requested</li>
                              <li>Use privacy-focused alternatives to mainstream services</li>
                              <li>Regularly audit and clean up old accounts and data</li>
                              <li>Use temporary email services for one-time signups</li>
                              <li>Consider using different identities for different contexts</li>
                              <li>Opt out of data collection when possible</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Digital Footprint Management:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Regularly review and adjust privacy settings on all platforms</li>
                              <li>Use privacy-enhancing tools like VPNs and privacy-focused browsers</li>
                              <li>Consider the privacy implications before posting content online</li>
                              <li>Remove sensitive personal information from data broker sites</li>
                              <li>Use search engines that don't track you</li>
                              <li>Be cautious with location sharing and geotags</li>
                            </ul>
                          </div>
                          
                          <p>Remember that once information is online, it can be difficult or impossible to completely remove it - think before you share.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-2">
                      <AccordionTrigger>Protecting Against Advanced Threats</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Some threats require more sophisticated protection strategies, especially if you're a high-value target.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Against Targeted Attacks:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use security keys (like YubiKey) for authentication</li>
                              <li>Consider using air-gapped computers for highly sensitive activities</li>
                              <li>Implement network segmentation at home</li>
                              <li>Use Tails or other security-focused operating systems for sensitive work</li>
                              <li>Be aware of physical security and shoulder surfing</li>
                              <li>Consider operational security in your communication patterns</li>
                              <li>Regularly audit authorized logins and sessions</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Against Sophisticated Malware:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Use application sandboxing</li>
                              <li>Implement application whitelisting</li>
                              <li>Consider using security-focused Linux distributions</li>
                              <li>Use virtual machines for isolating activities</li>
                              <li>Employ behavior-based malware detection</li>
                              <li>Regularly scan for rootkits and persistent threats</li>
                              <li>Use bootable security tools to inspect systems</li>
                            </ul>
                          </div>
                          
                          <p>The level of protection should be proportional to your threat model - assess your risks and implement appropriate measures.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-3">
                      <AccordionTrigger>Securing Smart Home & IoT Devices</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>Smart devices often have weak security, creating vulnerabilities in your home network.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">IoT Security Measures:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Change default passwords immediately on all devices</li>
                              <li>Create a separate network for IoT devices</li>
                              <li>Regularly update firmware on all devices</li>
                              <li>Disable unnecessary features and services</li>
                              <li>Research security before purchasing - prioritize devices with good update histories</li>
                              <li>Consider the privacy implications of devices with cameras or microphones</li>
                              <li>Implement network-level protection (firewall rules, IDS/IPS)</li>
                              <li>Physically protect critical devices</li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Critical Considerations:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Limit cloud dependencies when possible</li>
                              <li>Consider local-only smart home hubs and automation</li>
                              <li>Regularly audit connected devices on your network</li>
                              <li>Monitor network traffic for suspicious activities</li>
                              <li>Set up alerts for unauthorized access attempts</li>
                              <li>Have a plan for when devices reach end-of-support</li>
                            </ul>
                          </div>
                          
                          <p>Smart devices add convenience but also expand your attack surface - secure them accordingly.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="item-4">
                      <AccordionTrigger>Creating a Personal Security Plan</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 text-sm">
                          <p>A systematic approach to security helps ensure comprehensive protection aligned with your specific needs.</p>
                          
                          <div>
                            <h4 className="font-medium mb-2">Developing Your Plan:</h4>
                            <ol className="list-decimal pl-5 space-y-1">
                              <li>Identify your assets (data, devices, accounts)</li>
                              <li>Assess their importance and sensitivity</li>
                              <li>Identify threats and risks specific to you</li>
                              <li>Implement appropriate security measures</li>
                              <li>Create procedures for regular maintenance and updates</li>
                              <li>Develop incident response plans</li>
                              <li>Schedule regular security reviews</li>
                            </ol>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Documentation to Maintain:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Inventory of devices and accounts</li>
                              <li>Recovery methods for critical accounts</li>
                              <li>Backup verification procedures</li>
                              <li>Security contact information</li>
                              <li>Security recovery plans</li>
                              <li>Secure storage for important documentation</li>
                            </ul>
                          </div>
                          
                          <p>Your security plan should evolve as your digital life and the threat landscape change - review and update it regularly.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
                <CardFooter>
                  <p className="text-xs text-muted-foreground">Advanced security requires a balance between protection and usability - find what works for your specific needs and risk profile.</p>
                </CardFooter>
              </Card>
            </div>
            
            <Card className="border-green-500/20">
              <CardHeader>
                <CardTitle className="text-xl">What to Do If You've Been Hacked</CardTitle>
                <CardDescription>Step-by-step guide to recovering from a security incident</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Immediate Actions</h3>
                    <ol className="list-decimal pl-5 space-y-3">
                      <li>
                        <span className="font-medium">Disconnect from the internet</span>
                        <p className="text-sm text-muted-foreground mt-1">Remove your device from networks to prevent further data theft or damage.</p>
                      </li>
                      <li>
                        <span className="font-medium">Change critical passwords from a different device</span>
                        <p className="text-sm text-muted-foreground mt-1">Start with email and financial accounts. Use a secure device that hasn't been compromised.</p>
                      </li>
                      <li>
                        <span className="font-medium">Enable two-factor authentication</span>
                        <p className="text-sm text-muted-foreground mt-1">Add this extra layer of security to prevent future unauthorized access.</p>
                      </li>
                      <li>
                        <span className="font-medium">Contact financial institutions</span>
                        <p className="text-sm text-muted-foreground mt-1">Alert banks and credit card companies about potential fraud.</p>
                      </li>
                      <li>
                        <span className="font-medium">Check for suspicious activity</span>
                        <p className="text-sm text-muted-foreground mt-1">Review recent account activities, transactions, and emails sent from your accounts.</p>
                      </li>
                    </ol>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Recovery Process</h3>
                    <ol className="list-decimal pl-5 space-y-3 start-[6]">
                      <li>
                        <span className="font-medium">Clean up your devices</span>
                        <p className="text-sm text-muted-foreground mt-1">Run anti-malware scans, or consider a complete system reset if the compromise is severe.</p>
                      </li>
                      <li>
                        <span className="font-medium">Update all software</span>
                        <p className="text-sm text-muted-foreground mt-1">Install all available security updates for operating systems and applications.</p>
                      </li>
                      <li>
                        <span className="font-medium">Review connected applications</span>
                        <p className="text-sm text-muted-foreground mt-1">Check and revoke access for suspicious third-party apps connected to your accounts.</p>
                      </li>
                      <li>
                        <span className="font-medium">Monitor your accounts</span>
                        <p className="text-sm text-muted-foreground mt-1">Set up alerts for suspicious activities and regularly check for unauthorized changes.</p>
                      </li>
                      <li>
                        <span className="font-medium">Notify contacts if necessary</span>
                        <p className="text-sm text-muted-foreground mt-1">Let contacts know if your accounts were used to send out malicious content.</p>
                      </li>
                    </ol>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Long-term Protection</h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="border rounded-md p-4 space-y-2">
                      <h4 className="font-medium">Identity Monitoring</h4>
                      <p className="text-sm">Consider identity theft monitoring services that alert you to potential fraud. Check your credit reports regularly.</p>
                    </div>
                    
                    <div className="border rounded-md p-4 space-y-2">
                      <h4 className="font-medium">Review Your Security</h4>
                      <p className="text-sm">Identify how the breach occurred and strengthen those weak points. Consider advanced security measures.</p>
                    </div>
                    
                    <div className="border rounded-md p-4 space-y-2">
                      <h4 className="font-medium">Learn from the Incident</h4>
                      <p className="text-sm">Understanding what happened can help prevent future breaches. Research similar attacks and how to prevent them.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-amber-950/20 border border-amber-900/30 rounded-md p-4">
                  <h3 className="font-medium text-amber-400 mb-2">When to Seek Professional Help</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>If financial theft or identity theft has occurred</li>
                    <li>If business or highly sensitive personal data was compromised</li>
                    <li>If you're unable to remove persistent malware</li>
                    <li>If you're experiencing ongoing unauthorized access despite recovery efforts</li>
                    <li>If you're unsure about the extent of the breach</li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter>
                <p className="text-xs text-muted-foreground">Remember: Document everything during your recovery process - it may be needed for reporting to authorities or for insurance claims.</p>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="assistant" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Bot className="h-6 w-6 text-truth" />
                  Privacy & Security AI Assistant
                </CardTitle>
                <CardDescription>
                  Get expert advice on privacy concerns, hacking threats, and security best practices
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PrivacyAIChat />
              </CardContent>
              <CardFooter>
                <p className="text-xs text-muted-foreground">This AI is specifically trained to provide privacy and security guidance. You can upload images for analysis of potential security concerns.</p>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
      </div>
    </div>
  );
};

// Component for creating expandable privacy/security topic cards
const PrivacyCard = ({ 
  title, 
  description, 
  icon, 
  content 
}: { 
  title: string;
  description: string;
  icon: React.ReactNode;
  content: Array<{title: string, content: string}>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <Card className="overflow-hidden transition-all duration-300">
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">{title}</CardTitle>
          {icon}
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      <div className={`transition-all duration-300 ${isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"} overflow-hidden`}>
        <CardContent className="space-y-4 pt-0">
          <Separator />
          <ScrollArea className="h-80 pr-4">
            <div className="space-y-6">
              {content.map((item, index) => (
                <div key={index} className="space-y-2">
                  <h4 className="font-medium text-truth">{item.title}</h4>
                  <p className="text-sm text-muted-foreground">{item.content}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </div>
      
      <CardFooter className="pt-0">
        <button
          className="text-sm text-truth hover:underline flex items-center gap-1"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show less" : "Learn more"}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={`transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </CardFooter>
    </Card>
  );
};

export default PrivacyAndHacking;
